<template>
  <div class="video-frame">
    <!-- Video Frame Header -->
    <div class="video-header">
      <div class="d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <h6 class="mb-0 mr-3">Video Conference</h6>
          <div class="video-status">
            <span
              class="status-indicator"
              :class="{
                'active': isActive,
                'inactive': !isActive
              }"
            ></span>
            {{ isActive ? 'Live' : 'Not Started' }}
          </div>
        </div>

        <!-- Teacher Conference Controls -->
        <div v-if="userRole === 'teacher'" class="conference-controls">
          <!-- Show completed message if lesson is finished -->
          <b-badge v-if="webinarStatus === 'completed'" variant="secondary" pill>
            <feather-icon icon="CheckCircleIcon" size="14" class="mr-1" />
            Lesson Completed
          </b-badge>

          <!-- Start Conference Button -->
          <b-button
            v-else-if="!isActive"
            variant="success"
            size="sm"
            @click="handleStartConference"
            :disabled="isStarting"
          >
            <b-spinner v-if="isStarting" small class="mr-1" />
            <feather-icon v-else icon="VideoIcon" size="16" class="mr-1" />
            Start Conference
          </b-button>

          <!-- Stop Conference Button -->
          <b-button
            v-else
            variant="danger"
            size="sm"
            @click="handleStopConference"
            :disabled="isStopping"
          >
            <b-spinner v-if="isStopping" small class="mr-1" />
            <feather-icon v-else icon="VideoOffIcon" size="16" class="mr-1" />
            Stop Conference
          </b-button>
        </div>
      </div>
    </div>

    <!-- Jitsi Meet iframe -->
    <div class="video-content">
      <div v-if="!isActive" class="waiting-state">
        <div class="text-center py-5">
          <feather-icon icon="VideoIcon" size="48" class="text-muted" />
          <h5 class="mt-3 text-muted">Waiting for lesson to start</h5>
          <p class="text-muted">The teacher will start the video session shortly</p>
        </div>
      </div>

      <iframe
        v-else
        ref="jitsiFrame"
        :src="enhancedJitsiUrl"
        width="100%"
        height="100%"
        frameborder="0"
        allow="camera; microphone; display-capture; fullscreen; autoplay"
        allowfullscreen
        @load="onIframeLoad"
        @error="onIframeError"
      ></iframe>
    </div>

    <!-- Video Controls (basic) -->
    <div class="video-controls" v-if="isActive">
      <div class="d-flex justify-content-center align-items-center py-2">
        <feather-icon icon="InfoIcon" size="14" class="mr-1 text-primary" />
        <small class="text-muted">
          Video controls are at the bottom of the video window. Hover over the video to see them.
        </small>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VideoFrame',
  props: {
    jitsiUrl: {
      type: String,
      default: ''
    },
    roomName: {
      type: String,
      default: ''
    },
    userName: {
      type: String,
      default: 'User'
    },
    isActive: {
      type: Boolean,
      default: false
    },
    webinarId: {
      type: [String, Number],
      required: true
    },
    userRole: {
      type: String,
      default: 'student'
    },
    webinarStatus: {
      type: String,
      default: 'scheduled'
    }
  },
  data() {
    return {
      isLoading: true,
      hasError: false,
      connectionAttempts: 0,
      maxRetries: 3,
      isStarting: false,
      isStopping: false
    }
  },
  computed: {
    enhancedJitsiUrl() {
      if (!this.jitsiUrl || !this.roomName) return ''

      // Build enhanced URL with configuration parameters
      const baseUrl = `https://meet.jit.si/${this.roomName}`
      const params = new URLSearchParams()

      // User configuration
      params.append('userInfo.displayName', this.userName)

      // Interface configuration - minimal to ensure controls are visible
      params.append('config.startWithAudioMuted', 'false')
      params.append('config.startWithVideoMuted', 'false')
      params.append('config.prejoinPageEnabled', 'false')
      params.append('config.disableInviteFunctions', 'true')

      // Ensure toolbar is always visible
      params.append('config.toolbarAlwaysVisible', 'true')
      params.append('interfaceConfig.TOOLBAR_ALWAYS_VISIBLE', 'true')

      // Hide Jitsi branding but keep controls
      params.append('interfaceConfig.SHOW_JITSI_WATERMARK', 'false')
      params.append('interfaceConfig.SHOW_WATERMARK_FOR_GUESTS', 'false')
      params.append('interfaceConfig.MOBILE_APP_PROMO', 'false')

      return `${baseUrl}?${params.toString()}`
    }
  },
  watch: {
    isActive(newVal) {
      if (newVal && this.jitsiUrl) {
        this.$nextTick(() => {
          this.initializeJitsi()
        })
      }
    },
    jitsiUrl(newVal) {
      if (newVal && this.isActive) {
        this.$nextTick(() => {
          this.initializeJitsi()
        })
      }
    }
  },
  mounted() {
    if (this.isActive && this.jitsiUrl) {
      this.initializeJitsi()
    }
  },
  methods: {
    initializeJitsi() {
      // Additional Jitsi configuration can be added here
      // For now, we're using the iframe approach which is simpler
      console.log('Jitsi Meet initialized for room:', this.roomName)
    },

    onIframeLoad() {
      this.isLoading = false
      this.hasError = false
      this.connectionAttempts = 0
      console.log('Jitsi iframe loaded successfully')

      // You can add additional iframe communication here
      // For example, listening to Jitsi events via postMessage
      this.setupIframeListeners()
    },

    onIframeError(error) {
      console.error('Jitsi iframe failed to load:', error)
      this.hasError = true
      this.isLoading = false
      this.connectionAttempts++

      // Auto-retry logic
      if (this.connectionAttempts < this.maxRetries) {
        setTimeout(() => {
          console.log(`Retrying Jitsi connection (attempt ${this.connectionAttempts + 1}/${this.maxRetries})`)
          this.retryConnection()
        }, 3000)
      } else {
        this.$emit('connection-failed', {
          error: 'Failed to connect to video conference after multiple attempts',
          attempts: this.connectionAttempts
        })
      }
    },

    retryConnection() {
      this.isLoading = true
      this.hasError = false
      // Force iframe reload by updating src
      const iframe = this.$refs.jitsiFrame
      if (iframe) {
        iframe.src = iframe.src
      }
    },

    setupIframeListeners() {
      // Listen for messages from the Jitsi iframe
      window.addEventListener('message', this.handleJitsiMessage)
    },

    handleJitsiMessage(event) {
      // Handle messages from Jitsi Meet iframe
      // This allows communication between the parent and Jitsi
      if (event.origin !== 'https://meet.jit.si') {
        return
      }

      const data = event.data

      // Jitsi Meet API events
      switch (data.type) {
        case 'video-conference-joined':
          console.log('User joined Jitsi conference')
          this.hasError = false
          this.$emit('conference-joined', {
            roomName: this.roomName,
            participantId: data.participantId,
            displayName: data.displayName
          })
          break

        case 'video-conference-left':
          console.log('User left Jitsi conference')
          this.$emit('conference-left', {
            roomName: this.roomName,
            participantId: data.participantId
          })
          break

        case 'participant-joined':
          console.log('Participant joined:', data)
          this.$emit('participant-joined', {
            participantId: data.id,
            displayName: data.displayName,
            timestamp: new Date().toISOString()
          })
          break

        case 'participant-left':
          console.log('Participant left:', data)
          this.$emit('participant-left', {
            participantId: data.id,
            timestamp: new Date().toISOString()
          })
          break

        case 'video-mute-status-changed':
          console.log('Video mute status changed:', data)
          this.$emit('video-status-changed', {
            participantId: data.participantId,
            muted: data.muted
          })
          break

        case 'audio-mute-status-changed':
          console.log('Audio mute status changed:', data)
          this.$emit('audio-status-changed', {
            participantId: data.participantId,
            muted: data.muted
          })
          break

        case 'screen-share-status-changed':
          console.log('Screen share status changed:', data)
          this.$emit('screen-share-changed', {
            participantId: data.participantId,
            sharing: data.on
          })
          break

        case 'video-conference-ready':
          console.log('Jitsi conference ready')
          this.isLoading = false
          this.hasError = false
          break

        case 'error-occurred':
          console.error('Jitsi error occurred:', data)
          this.hasError = true
          this.$emit('conference-error', data)
          break

        default:
          // Log other events for debugging
          console.log('Jitsi event:', data.type, data)
          break
      }
    },

    // Enhanced Jitsi configuration
    getJitsiConfig() {
      return {
        roomName: this.roomName,
        width: '100%',
        height: '100%',
        parentNode: document.querySelector('#jitsi-container'),
        configOverwrite: {
          startWithAudioMuted: false,
          startWithVideoMuted: false,
          enableWelcomePage: false,
          enableCalendarIntegration: false,
          prejoinPageEnabled: false,
          disableInviteFunctions: true,
          toolbarButtons: [
            'microphone', 'camera', 'closedcaptions', 'desktop',
            'fullscreen', 'fodeviceselection', 'hangup', 'chat',
            'etherpad', 'settings', 'videoquality', 'filmstrip',
            'stats', 'shortcuts'
          ],
          hideDisplayName: false,
          disableProfile: true
        },
        interfaceConfigOverwrite: {
          TOOLBAR_TIMEOUT: 4000,
          SHOW_JITSI_WATERMARK: false,
          SHOW_WATERMARK_FOR_GUESTS: false,
          SHOW_BRAND_WATERMARK: false,
          DEFAULT_LOGO_URL: '',
          DEFAULT_WELCOME_PAGE_LOGO_URL: '',
          HIDE_INVITE_MORE_HEADER: true,
          MOBILE_APP_PROMO: false
        },
        userInfo: {
          displayName: this.userName,
          email: ''
        }
      }
    },

    async handleStartConference() {
      if (this.isStarting) return

      this.isStarting = true
      try {
        await this.$store.dispatch('lessonRoom/startConference', this.webinarId)
        this.$emit('conference-started')
        this.$bvToast.toast('Video conference has been started', {
          title: 'Conference Started',
          variant: 'success',
          solid: true
        })
      } catch (error) {
        console.error('Failed to start conference:', error)
        this.$bvToast.toast(error.message || 'Failed to start conference', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.isStarting = false
      }
    },

    async handleStopConference() {
      if (this.isStopping) return

      this.isStopping = true
      try {
        await this.$store.dispatch('lessonRoom/stopConference', this.webinarId)
        this.$emit('conference-stopped')
        this.$bvToast.toast('Video conference has been stopped', {
          title: 'Conference Stopped',
          variant: 'info',
          solid: true
        })
      } catch (error) {
        console.error('Failed to stop conference:', error)
        this.$bvToast.toast(error.message || 'Failed to stop conference', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.isStopping = false
      }
    }
  },

  beforeDestroy() {
    // Cleanup event listeners
    window.removeEventListener('message', this.handleJitsiMessage)
  }
}
</script>

<style lang="scss" scoped>
.video-frame {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #000;
  border-radius: 8px;
  overflow: hidden;

  .video-header {
    background: rgba(255, 255, 255, 0.95);
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #e3e3e3;

    h6 {
      color: #2c3e50;
      font-weight: 600;
    }

    .video-status {
      display: flex;
      align-items: center;
      font-size: 0.875rem;
      font-weight: 500;

      .status-indicator {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        margin-right: 0.5rem;

        &.active {
          background-color: #28a745;
          box-shadow: 0 0 0 2px rgba(40, 167, 69, 0.3);
          animation: pulse 2s infinite;
        }

        &.inactive {
          background-color: #6c757d;
        }
      }

      @keyframes pulse {
        0% {
          box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.7);
        }
        70% {
          box-shadow: 0 0 0 6px rgba(40, 167, 69, 0);
        }
        100% {
          box-shadow: 0 0 0 0 rgba(40, 167, 69, 0);
        }
      }
    }

    .conference-controls {
      .btn {
        display: inline-flex;
        align-items: center;
        font-weight: 500;
        padding: 0.5rem 1rem;
        white-space: nowrap;

        svg {
          flex-shrink: 0;
        }
      }
    }
  }

  .video-content {
    flex: 1;
    position: relative;
    background:  #1a1a1a;
    min-height: 600px;

    iframe {
      border: none;
      background: #1a1a1a;
      min-height: 600px;
    }

    .waiting-state {
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;

      .text-center {
        h5 {
          color: rgba(255, 255, 255, 0.9);
        }
        p {
          color: rgba(255, 255, 255, 0.7);
        }
      }
    }
  }

  .video-controls {
    background: rgba(255, 255, 255, 0.95);
    border-top: 1px solid #e3e3e3;

    small {
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .video-frame {
    .video-content {
      min-height: 250px;
    }
  }
}
</style>